import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo, useState } from 'react';

import { settings } from '@/lib/db';
import type { ApiSourceDescriptor, HistoryReach, TrackSource } from '@/lib/sources';
import { createTrackSource } from '@/lib/sources';

import { configuredSources, credentialsFrom, preferredSource, relayFrom } from './credentials';

/**
 * The settings rows every source-aware component reads from.
 *
 * One live query over the whole settings table, not one per key — the same
 * trade `useSettings` already made, and for the same reason: Dexie re-runs a
 * live query whenever the table it read changes, so eight per-field
 * subscriptions would all fire on every keystroke in the settings form.
 *
 * `null` means "the database has not answered yet", which is not the same as
 * "nothing is configured" and must not render as it.
 */
export function useSourceSettings(): Record<string, unknown> | null {
  return useLiveQuery(() => settings.getAll(), [], null);
}

/** Sources ready to use right now, or `null` while the database is opening. */
export function useConfiguredSources(): ApiSourceDescriptor[] | null {
  const rows = useSourceSettings();
  return useMemo(() => (rows === null ? null : configuredSources(rows)), [rows]);
}

/** The source a one-click action should use, or `null` while the database is opening. */
export function usePreferredSource(): ApiSourceDescriptor | null | undefined {
  const rows = useSourceSettings();
  return useMemo(() => (rows === null ? null : preferredSource(rows)), [rows]);
}

/**
 * A live adapter for one source, built from the pilot's stored credentials.
 *
 * Rebuilt whenever the credentials change, which matters more than it looks:
 * the adapters hold state — the ADS-B one caches a downloaded day file — and
 * nothing built from an old key should survive the pilot changing it.
 */
export function useTrackSource(
  descriptor: ApiSourceDescriptor | undefined,
): TrackSource | undefined {
  const rows = useSourceSettings();
  const credentials = useMemo(
    () => (rows === null || descriptor === undefined ? {} : credentialsFrom(rows, descriptor.id)),
    [rows, descriptor],
  );
  const relay = rows === null ? undefined : relayFrom(rows);
  // The dependency is the credential *values*, not the object identity that
  // `credentialsFrom` creates fresh on every settings write.
  const fingerprint = JSON.stringify(credentials);

  return useMemo(
    () =>
      descriptor === undefined
        ? undefined
        : createTrackSource(descriptor.id, JSON.parse(fingerprint) as Record<string, string>, {
            relay,
          }),
    [descriptor, fingerprint, relay],
  );
}

/**
 * How far back a source reaches, or `null` while it is being worked out.
 *
 * FR24's answer is documented and immediate; the ADS-B globes measure theirs
 * with a handful of `HEAD` requests, so there is a moment where the search
 * screen has no answer yet and says so. `undefined` for a source that cannot
 * say at all.
 */
export function useSourceHistory(source: TrackSource | undefined): HistoryReach | null | undefined {
  const [answer, setAnswer] = useState<{ source: TrackSource; reach: HistoryReach } | null>(null);

  useEffect(() => {
    if (source?.history === undefined) {
      return;
    }
    let cancelled = false;
    void source.history().then((reach) => {
      if (!cancelled) {
        setAnswer({ source, reach });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [source]);

  if (source?.history === undefined) {
    return undefined;
  }
  // An answer about the previous source is no answer about this one.
  return answer?.source === source ? answer.reach : null;
}
